// Treasurer-visible copy for preflight warnings, classifier labels, schedule badges and
// the filing modals. Every entry the server also owns comes from the generated
// Features.Finance.Filing.Preflight.PreflightCopy; the entries written below are client-only copy
// with no C# counterpart.
//
// The API leaves PreflightWarning.warningText as-is. The frontend renders these
// literals instead, looked up by `(warningType, classifier?)`, and falls back to
// warning.warningText when no entry is present.

import {
  FINANCE_DEFAULT_SETTLEMENT_AGING_THRESHOLD_DAYS,
  FINANCE_MAX_SETTLEMENT_AGING_THRESHOLD_DAYS,
  FINANCE_MIN_SETTLEMENT_AGING_THRESHOLD_DAYS,
  FINANCE_PREFLIGHT_COPY,
} from '../generated/constants.gen';
import { SubmissionPackageExceptionType } from '../generated/enums.gen';
import { WarningType } from './preflight';

export const PreflightCopy = {
  ...FINANCE_PREFLIGHT_COPY,

  /** The close-hub ledger's per-row form of the same verdict — the advisory pill's
   *  text label (never colour alone) on a row the purpose warning flagged. Short
   *  because it sits inline in the Category column beside the category chip; the
   *  modal body above stays the full, plural explanation. */
  FederalDisbursementPurposeMissingLedgerLabel: 'Purpose too vague',

  /** Tooltip for the ledger pill above. Deliberately jurisdiction-NEUTRAL even though
   *  the verdict behind it is federal-gated: it renders from `TriageLedger`, a ledger
   *  every filer sees, so it teaches what an adequate purpose looks like without naming
   *  a regulator (the state-filer copy discipline asserted by
   *  `apps/web/src/test/finance-copy-fec-scope.test.ts`). Examples are the regulation's
   *  own, matching the modal body. */
  FederalDisbursementPurposeMissingLedgerTooltip:
    'This purpose is blank or too generic to file. Say what the money '
    + 'actually bought — "media", "salary", "polling" and "catering costs" '
    + 'all work.',

  /** per-row marker for an already-reported row folded back into an OPEN
   *  report's itemized schedule (its `LedgerStatus == Reported`, but the period is not
   *  yet filed). It is ON the report but NOT on the next upload, so it reads distinctly
   *  from the "will be on the filing" rows — the treasurer can still tell what the next
   *  export contains. */
  ScheduleReportedRowLabel: 'Already reported',

  /** Issue #107 — off-filing label for a Pending row in the Form 3 preview's single
   *  "in this period, not on this filing" section. The true reason it's off the filing
   *  is that it hasn't been approved (Confirmed) yet — NOT that funds haven't settled
   *  ("awaiting settlement" was the wrong framing; settlement is not a filing gate). */
  ScheduleOffFilingNeedsReviewLabel: 'Needs review',

  /** §32 — excluded-row label for a Pending row the electronic export can't carry
   *  (AL In-Kind "Other"): the remedy is portal hand-entry, not export + upload, so
   *  the "Pending at filing" badge would point at a dead-end. `{portal}` is
   *  substituted at the render boundary with the jurisdiction portal name (never
   *  hard-coded), e.g. "Add manually in FCPA portal". */
  ScheduleAddManuallyInPortalLabel: 'Add manually in {portal}',

  /** #644 — per-row status marker (the report screen's one added "sent to the state?" column,
   *  the home the re-send action will live in): a row that has already gone to the state — the
   *  shared already-sent union (`Reported` OR an active upload-member), never ledger status
   *  alone. */
  ScheduleRowSentLabel: 'Sent',

  /** #644 — per-row status marker for an EDI row not yet sent to the state (the rows the next
   *  export/upload will carry). An In-Kind "Other" row the export omits reads the
   *  `ScheduleAddManuallyInPortalLabel` portal-hand-entry marker instead. (Loans also never ride
   *  the export, but they aren't itemized rows on these tables — they feed the cash-on-hand /
   *  federal schedules — so no loan row renders this marker.) */
  ScheduleRowNotSentLabel: 'Not yet sent',

  /** #647 — the re-send action shown in the status column on an already-sent EDI row: a deliberate,
   *  audited re-send of ONE already-sent transaction the state lost (#645). Only appears on
   *  already-sent rows (a not-yet-sent row goes out on the next export anyway). */
  ScheduleRowResendActionLabel: 'Re-send',

  /** #647 — the status a row reads once a re-send is queued: the next export will force-include it
   *  once, and the action disables so re-sends can't be piled up. */
  ScheduleRowResendQueuedLabel: 'Re-send queued',

  /** #647 — title of the confirm dialog the re-send action opens. */
  ResendConfirmTitle: 'Re-send this transaction to the portal?',

  /** #647 — the HONEST warning the re-send confirm dialog shows. A re-send is append-only: it does
   *  not replace or update whatever the portal already has, so if the portal still has any version of
   *  this transaction, re-sending would DUPLICATE it. The treasurer must delete it in the portal
   *  first. Verbatim per the issue — do not soften. */
  ResendConfirmWarning:
    'If the portal already has this transaction — even an older version — do not re-send it. '
    + 'Delete it in the portal first, then re-send.',

  /** #647 — confirm-button label on the re-send dialog. */
  ResendConfirmAccept: 'Re-send it',

  /** #647 — cancel-button label on the re-send dialog (cancelling records nothing). */
  ResendConfirmCancel: 'Cancel',

  /**
   * Issue #186 — the "mark filed in portal" confirmation body for a Louisiana (LEADERS)
   * filer. LA is upload-≠-filing: the treasurer UPLOADS the generated `.PDP` to the LEADERS
   * portal, so the append-only trust contract is worded around "uploaded" — never "submitted"
   * or "attested". `{N}` is substituted with the pluralized transaction phrase. Asserted
   * verbatim by preflightCopy.test.ts (this is a frontend-only surface — the server emits no
   * confirmation copy, so there is no C# mirror).
   */
  LouisianaMarkFiledConfirmBody:
    "This records {N} as filed in the Louisiana LEADERS portal — they'll show reported and "
    + "won't be re-exported. Only do this if you actually uploaded them to the LEADERS portal.",

  /**
   * #296 / #860 — the confirm-filed body for an EMAIL-transport filer (the report is emailed to a
   * receiving office; there is no portal and no confirmation number), so the copy is worded around
   * "emailed" — never "uploaded" / "in the portal". `{N}` is the pluralized transaction phrase;
   * `{office}` composes the SERVED receiving-office name (`profile.portalName`) so a second
   * email-transport jurisdiction reads its own office (NC → "NCSBE"; Mississippi → "the Secretary of
   * State" / "the Circuit Clerk") with no component edit — NC renders identically. Frontend-only
   * surface (no C# mirror); asserted by preflightCopy.test.ts.
   */
  EmailMarkFiledConfirmBody:
    "This records {N} as filed — they'll show reported and can only be changed by reopening. "
    + 'Only confirm this once you have emailed your report file to {office}.',

  /**
   * The receipt chase (a `proofOfFiling` jurisdiction — Mississippi): a report confirmed filed with no
   * confirmation of receipt recorded. `{office}` is the served receiving-office name ("Secretary of
   * State" / "Circuit Clerk", composed through `receivingOfficePhrase`) — never "portal". Frontend-only.
   */
  ReceiptChaseTitle: "Record {office}'s confirmation of receipt",
  ReceiptChaseBody:
    "This report is marked filed, but no confirmation of receipt from {office} is recorded yet. "
    + "That confirmation is your proof the report arrived on time — add it here when it comes in.",
  ReceiptChaseDismiss: "I won't get one — stop reminding me",
  ReceiptChaseDismissConfirm:
    'Stop reminding you about this confirmation? Your decision is recorded in the report history.',

  /**
   * Edit-after-send (a `proofOfFiling` jurisdiction — Mississippi). Once the report has been emailed,
   * the sent file is the filing of record, and there is no partial amendment: any change means sending
   * the whole corrected report again. `{office}` as above; `{date}` is the formatted send date.
   */
  SentReportEditWarning:
    'You sent this report to {office} on {date}. If you change anything in it now, send the '
    + 'whole corrected report again — {office} only accepts complete reports, not partial corrections.',

  /**
   * #296 — the NC post-file self-send steps (rendered instead of SuccessPanelPortalStep for a NC
   * report, since NC has no portal and NCSBE issues no confirmation number). `{inbox}` is
   * substituted with the destination address from `filingDeliveryInstructions` on the report DTO
   * (#860 — the transport-neutral block). The cover always goes to the NCSBE main office (NCSBE
   * confirmed no per-office-level routing), so the cover step names it directly. The AL/VA/LA
   * SuccessPanelPortalStep literal is untouched — NC branches to its own copy path. Asserted verbatim
   * by preflightCopy.test.ts.
   */
  SuccessPanelNcCfdStep:
    'Email your report file to {inbox}. North Carolina has no filing portal, and NCSBE '
    + 'issues no confirmation number — your sent email is your receipt.',
  SuccessPanelNcCoverStep:
    'Deliver your signed CRO-1000 cover to the North Carolina State Board of Elections (NCSBE).',
  SuccessPanelNcAmendmentLine:
    'To amend, regenerate this report and email the updated file to NCSBE.',

  /**
   * #313 — the PRE-generate (still-Ready) note for an email-transport report (NC, MS), rendered
   * instead of the portal-upload note ("upload it to the {state} {portal}") since these files go by
   * EMAIL to the receiving office; there is no filing portal. `{office}` is the served receiving-office
   * phrase ("NCSBE" / "the Secretary of State"), `{state}` the state's display name — NC renders
   * byte-identically to its original wording. Frontend-only surface (no C# mirror, like
   * the #296 SuccessPanelNc* keys). Worded around email — never "upload" / "portal".
   */
  GenerateEmailStep:
    'Generate this report, then email it to {office}. {state} has no filing portal, so '
    + 'nothing is filed automatically — after you email your report to {office}, come back and mark '
    + 'it filed here.',

  /**
   * #313 — the post-generate success action for an email-filed report (NC, MS), rendered after
   * "Report generated — {file} downloaded." instead of the portal-upload action. `{office}` is the
   * served receiving-office phrase (NC → "NCSBE"; MS → "the Secretary of State") — never a portal.
   * Frontend-only surface (no C# mirror).
   */
  GenerateEmailSuccessAction:
    'Email this file to {office}, then come back and mark it filed.',

  /**
   * The success action when the download is a whole-package .zip (Mississippi's cover + itemized
   * schedules). The cover alone is not the filing, so the copy names every PDF in the download.
   */
  GenerateEmailPackageSuccessAction:
    'Email every PDF in this download (the cover page and its schedules) to {office}, then come back '
    + 'and mark it filed.',

  // ADR-016 — the cancellation chooser (Void / Delete / Restore). This is a
  // FRONTEND-ONLY dialog: the chooser surface has no server emitter, so — unlike
  // every other key in this object — these have NO `Features.Finance.Filing.Preflight.PreflightCopy`
  // C# mirror. The single contract is `preflightCopy.test.ts` (client); the
  // chooser component renders these verbatim so no engineer enum identifier ever
  // leaks into treasurer-facing text. Copy edits require a ticket. Treasurer-first,
  // plain-English; reviewed by a human for tone before acceptance.

  /** Dialog heading on a live / voided money row (the cancel fork). */
  ChooserDialogTitle: 'Take this off your report?',

  /** Dialog subtitle (accessible description) on a live / voided / refund row. */
  ChooserDialogSubtitle: 'Choose what should happen to this entry.',

  /**
   * The shared promise that LEADS the Void and Delete options — the unmissable
   * "this never reaches a report" line ADR-016 §9 leans on to blunt the
   * which-do-I-pick cost. (Restore is the exception — it brings a row BACK, so
   * it leads with {@link ChooserRestoreFraming} instead.)
   */
  ChooserNotOnReportPromise: 'This will not be on your report.',

  /** Void option — title (treasurer verb). */
  ChooserVoidOptionTitle: 'Void',

  /** Void option — the "happened but cancelled, kept & reversible" framing. */
  ChooserVoidOptionBody:
    "It really happened, but it's cancelled. We keep it as a record, and you "
    + 'can bring it back later.',

  /** Delete option — title (treasurer verb). */
  ChooserDeleteOptionTitle: 'Delete',

  /** Delete option — the "never happened, wrong import or mistake" framing. */
  ChooserDeleteOptionBody:
    'It never happened — a wrong import or a mistake. We remove it for good.',

  /** Delete option — the heavy permanent-removal warning (no type-to-confirm). */
  ChooserDeletePermanentLine: 'Permanent — this cannot be undone.',

  /** Restore option — title (treasurer verb, un-void). */
  ChooserRestoreOptionTitle: 'Restore',

  /** Restore option — the "bring this back for review" framing. */
  ChooserRestoreFraming: 'Bring this back for review.',

  /** Heading on a Reported (filed) money row — neither cancel nor delete. */
  ChooserReportedTitle: 'This entry is on a filed report',

  /** Reported money row — the amend MESSAGE (a message, not a route; ADR-007). */
  ChooserReportedAmendMessage:
    'This entry is on a filed report, so it cannot be voided or deleted here. '
    + 'To change it, amend that report.',

  // Inline error translations rendered inside the chooser when a mutation is
  // refused — the raw `DeleteErrorCodes` / 409 never reach the treasurer.

  /** 409 `DeleteHasDependentRefunds` — a non-voided refund still points at it. */
  ChooserDependentRefundsError:
    'This contribution has a refund linked to it. Delete or void the refund first.',

  /** 409 `DeleteReferencedByFiling` — a filing still references the row. */
  ChooserReferencedByFilingError:
    "This entry is referenced by a filing and can't be deleted. Amend the report instead.",

  /** 409 concurrency — the row changed under the treasurer; re-fetch and retry. */
  ChooserConcurrencyError:
    'This entry was updated somewhere else — refresh to see the latest, then try again.',

  // ADR-081 — 409 `DeleteSupportsFiledReport`. The refusal is not a permissions
  // problem and must not read as one: it says which filed report the entry is on and
  // what the treasurer does instead. Built as a function because naming the report is
  // the whole point of the refusal.

  /**
   * 409 `DeleteSupportsFiledReport` — the entry is on a report already sent to the
   * regulator, so it can't be destroyed whatever the filing calendar says.
   */
  ChooserSupportsFiledReportError: (reportName: string) =>
    `This entry is on ${reportName}, which has already been filed, so it can't be deleted. `
    + 'Amend that report to correct it, or void this entry to keep it off future filings.',

  /** The link out of that refusal to the report, where the amendment path lives. */
  ChooserSupportsFiledReportAmendLink: (reportName: string) => `Go to ${reportName}`,

  // ADR-016 step 2 — permanent Delete lives INSIDE the row detail (the
  // deliberate second step, reached by opening a row), gated by a heavy
  // confirm. The quick trailing action only ever excludes/restores.
  // Treasurer-first, plain-English; reuses ChooserDeleteOptionBody +
  // ChooserDeletePermanentLine for the body + permanence warning.

  /** Detail delete — the trigger + confirm-button label. */
  DetailDeleteButton: 'Delete permanently',

  /** Detail delete — confirm dialog heading. */
  DetailDeleteTitle: 'Delete this permanently?',

  /**
   * Detail delete — the bank-line re-import caveat (ADR-006 amendment): a
   * deleted bank line is removed from the ledger, but can reappear if the same
   * bank statement is imported again. Surfaced so the removal is never a silent
   * surprise.
   */
  DetailDeleteBankReimportNotice:
    'If you import this bank statement again later, this line can come back.',

  // Pending-at-filing UX (frontend-only — NOT part of the C# preflight contract).
  // Alabama's two-axis model: a report can be Filed in the portal while some
  // in-period transactions are still Pending at filing and haven't been exported
  // + uploaded to the FCPA portal yet, so they aren't reported (ADR-020). These
  // strings replace the false "all done" celebration with an actionable warning,
  // and drive the confirm-before-mark-filed dialog. Non-blocking throughout: the
  // portal-direct persona can legitimately mark a report filed with pending rows.
  // `{n}` is the count of transactions still Pending at filing.

  /** Heading of the amber warning shown in place of the success panel while
   *  in-period transactions are still Pending at filing. */
  PendingAtFilingWarningTitle: 'Some transactions still need to be reported',

  /** Body of the pending-at-filing warning — full singular/plural variants so the
   *  verb + pronoun agree with the count (never "1 transaction ... aren't"). Resolve
   *  via {@link pendingAtFilingWarningBody}; the plural substitutes `{N}` with the
   *  count. `{state}` + `{portal}` are substituted at the render boundary (never
   *  hard-coded "Alabama"/"FCPA portal") so the copy composes per jurisdiction — the
   *  same token convention as the sibling PendingAtFilingManualPortal / ManualPortalEntry
   *  bodies. Mirrors the PendingInPeriodSingular/Plural convention above. */
  PendingAtFilingWarningBodySingular:
    "1 transaction in this period isn't on this filing yet. Export the new "
    + "transaction and upload it to the {state} {portal} so it's reported.",
  PendingAtFilingWarningBodyPlural:
    "{N} transactions in this period aren't on this filing yet. Export the new "
    + "transactions and upload them to the {state} {portal} so they're reported.",

  /** §32 — body for pending rows the electronic export deliberately OMITS (AL
   *  In-Kind "Other"): Export says "nothing to export" for these, so the correct
   *  remedy is manual portal entry, not export + upload. Full singular/plural
   *  variants so the verb + pronoun agree with the count. `{state}` + `{portal}`
   *  are substituted at the render boundary (never hard-coded "Alabama"/"FCPA
   *  portal") so the copy composes per jurisdiction; the plural also substitutes
   *  `{N}`. Resolve via {@link pendingAtFilingManualPortalBody}. */
  PendingAtFilingManualPortalBodySingular:
    "1 in-kind \"Other\" contribution in this period can't be exported — "
    + 'add it directly in the {state} {portal} (nature "Other" with a short '
    + "description), then it's reported.",
  PendingAtFilingManualPortalBodyPlural:
    "{N} in-kind \"Other\" contributions in this period can't be exported — "
    + 'add them directly in the {state} {portal} (nature "Other" with a short '
    + "description), then they're reported.",

  /** §32 — the "enter these in the portal by hand" panel that surfaces CONFIRMED
   *  in-kind "Other" rows (the ones already ready to file, so they'd otherwise sit
   *  silently as "will be on the filing" while Export drops them). Distinct from the
   *  Pending body above: those rows aren't confirmed yet; these are, so the panel
   *  lists each with its values + a one-click "mark reported". Full singular/plural
   *  so the verb + pronoun agree with the count; `{state}` / `{portal}` substituted
   *  at the render boundary, plural also `{N}`. Resolve via {@link manualPortalEntryHeading}
   *  / {@link manualPortalEntryBody}. No em dashes (house copy law). */
  ManualPortalEntryHeadingSingular: 'Enter this in the {state} {portal} by hand',
  ManualPortalEntryHeadingPlural: 'Enter these in the {state} {portal} by hand',
  ManualPortalEntryBodySingular:
    'This in-kind "Other" contribution can\'t go in the upload file: {state} needs a '
    + 'description the electronic format has no field for. Add it directly in the '
    + '{portal} (nature "Other", with the description below), then mark it reported '
    + 'here so it stops showing as unfiled.',
  ManualPortalEntryBodyPlural:
    '{N} in-kind "Other" contributions can\'t go in the upload file: {state} needs a '
    + 'description the electronic format has no field for. Add each one directly in the '
    + '{portal} (nature "Other", with the description below), then mark it reported '
    + 'here so it stops showing as unfiled.',
  /** Per-row action on the manual-portal panel: flips Confirmed → Reported. */
  ManualPortalEntryMarkReportedAction: 'Mark as reported',
  /** Guardrail helper under the action: marking before it's actually in the portal
   *  would silently drop it, so the copy states the precondition (the undo is the
   *  safety net). `{portal}` substituted at the boundary. */
  ManualPortalEntryMarkReportedHint: "Only after you've added it in the {portal}.",
  /** The fixed value every row on this panel must carry in the portal's nature
   *  field (these rows are in-kind "Other" by definition). */
  ManualPortalEntryNatureValue: 'Other',

  /** §32 — replaces the export button's misleading "everything has already been
   *  uploaded" no-op message when the ONLY thing left is an un-emittable in-kind
   *  "Other" row (which the EDI can't carry). Points at the hand-entry panel instead
   *  of implying the work is done. `{portal}` substituted; plural also `{N}`. Resolve
   *  via {@link exportDeltaManualPortalNote}. */
  ExportDeltaOnlyManualPortalNoteSingular:
    'Nothing to upload electronically. This in-kind "Other" contribution can only be '
    + 'added in the {portal} by hand (see below).',
  ExportDeltaOnlyManualPortalNotePlural:
    'Nothing to upload electronically. These {N} in-kind "Other" contributions can only '
    + 'be added in the {portal} by hand (see below).',

  /** Confirm-dialog title shown before marking a report filed while in-period
   *  transactions are still Pending at filing. */
  PendingAtFilingConfirmTitle: 'Mark this report filed?',

  /** Confirm-dialog body — full singular/plural variants (see the warning body).
   *  Resolve via {@link pendingAtFilingConfirmBody}; the plural substitutes `{N}`. */
  PendingAtFilingConfirmBodySingular:
    "1 transaction in this period hasn't been exported and uploaded to the "
    + 'Alabama FCPA portal yet. Mark this report filed anyway?',
  PendingAtFilingConfirmBodyPlural:
    "{N} transactions in this period haven't been exported and uploaded to the "
    + 'Alabama FCPA portal yet. Mark this report filed anyway?',

  /** Confirm-dialog primary action — proceeds with the attestation. */
  PendingAtFilingConfirmAction: 'Mark filed anyway',

  /** Confirm-dialog cancel action — backs out without filing. */
  PendingAtFilingConfirmCancel: 'Go back',

  // ── Preflight action-label copy (frontend-only; no C# mirror) ──────────────
  // §30 — the per-warning "Dismiss" action read ambiguously as "handled" vs.
  // "ignore". Renamed to "Not applicable" with a helper line so a treasurer never
  // mistakes it for recording that they completed the portal step. The
  // attestation action ("Confirm and file anyway", AttestButtonLabel) is unchanged.

  /** Replaces the bare "Dismiss" label on a preflight warning's dismiss action. */
  DismissNotApplicableLabel: 'Not applicable',

  /** Helper line under the "Not applicable" action clarifying what it does NOT do. */
  DismissNotApplicableHelp:
    "Dismiss only if this doesn't apply — it does not record that you completed "
    + 'the portal step.',

  // §25 — Export vs File on the Electronic Submission panel. For an append-only
  // (portal-upload) jurisdiction, Export (incremental delta) and File Report (full
  // package) are ALTERNATIVES, not sequential steps — doing both double-reports the
  // same transactions. `{state}` is substituted at the render boundary.

  /** One-line clarifier that Export and File are alternatives (portal-upload only). */
  ExportVsFileClarifier:
    'Use Export for the incremental delta upload (recommended for {state}). '
    + 'File Report generates a full package — you only need one; '
    + "don't do both for the same transactions.",

  /** §25 — softer, non-alarming explanation shown in place of the raw red File error
   *  when a still-Ready report's transactions were already uploaded via Export (so
   *  there's no separate in-system package to generate). `{portal}` is substituted
   *  at the render boundary. */
  FilePortalNoPackageSoftNote:
    "These transactions were already uploaded via Export — there's no separate "
    + "package to generate. To change what's filed, amend in the {portal}.",

  // §34 — the Amend button + modal CTA on the report list implied the app performs
  // the amendment. For a portal-upload jurisdiction the app does NOT amend — the
  // treasurer amends directly in the portal — so the labels say so plainly.

  /** Replaces the "Amend" row button label (portal-upload jurisdictions). */
  RecordAmendmentButtonLabel: 'Record an amendment (done in portal)',

  /** Replaces the "Confirm Amendment" modal CTA (portal-upload jurisdictions). */
  RecordAmendmentModalCta: "I'll amend in the portal.",

  /**
   * Issue #219 — the amend-modal BODY for a self-contained-upload jurisdiction (LA/VA, i.e.
   * `isPortalUpload && !isAlabama`). The old shared body ("This will generate an amended
   * report… The original will be marked as Amended.") was in-system framing that contradicted
   * the portal CTA and gave no hint that you add a forgotten donation to the LEDGER first. This
   * states the real flow in plain steps: it regenerates from the current ledger (add forgotten
   * items first), you upload the new file to the portal, and the original is marked Amended once
   * the correction is filed (ADR-050). Frontend-only surface — no C# mirror (matches the sibling
   * `RecordAmendmentButtonLabel` / `RecordAmendmentModalCta`). State-neutral (no hardcoded portal
   * name), so it serves LA and VA alike.
   */
  RecordAmendmentPortalUploadBody:
    'This rebuilds the report from your current donations — so add anything you forgot '
    + "before you amend. You'll get an updated file to upload to your state's filing portal, "
    + 'then mark it filed. Your original stays on record, marked Amended once you file the correction.',

  /**
   * Email-filed variant of {@link RecordAmendmentPortalUploadBody} for North Carolina — the
   * report is self-contained (rebuilt whole from the ledger, like LA/VA) but leaves the app by
   * EMAIL to NCSBE, not a portal upload (`filingProfile.filingTransport === 'email'`). Same
   * rebuild-and-mark-filed flow, worded around email so an NC amendment never tells the treasurer
   * to "upload to your state's filing portal" (NC has no portal). Rendered when the served
   * transport is `email` — the branch keys off the transport, never a `stateCode === 'NC'` boolean.
   * `{office}` is the served receiving-office phrase (NC → "NCSBE"; MS → "the Secretary of State").
   * Frontend-only surface — no C# mirror (matches the #296 SuccessPanelNc* keys).
   */
  RecordAmendmentEmailBody:
    'This rebuilds the report from your current donations — so add anything you forgot '
    + "before you amend. You'll get an updated file to email to {office}, then mark it filed. "
    + 'Your original stays on record, marked Amended once you file the correction.',

  /** Email-filed variant of {@link RecordAmendmentButtonLabel} — the treasurer emails the amendment
   *  to `{office}`; the app does not file it. Worded around email, never "in portal". */
  RecordAmendmentEmailButtonLabel: 'Record an amendment (emailed to {office})',

  /** Email-filed variant of {@link RecordAmendmentModalCta} (`{office}` token as above) — the confirm CTA that matches
   *  the {@link RecordAmendmentEmailBody} email flow. Never "I'll amend in the portal." */
  RecordAmendmentEmailModalCta: "I'll email the amendment to {office}.",
} as const;

/** Settlement-aging threshold bounds, so the settings UI can label the default and validate its input. */
export const SettlementAgingThresholdBounds = {
  Default: FINANCE_DEFAULT_SETTLEMENT_AGING_THRESHOLD_DAYS,
  Min: FINANCE_MIN_SETTLEMENT_AGING_THRESHOLD_DAYS,
  Max: FINANCE_MAX_SETTLEMENT_AGING_THRESHOLD_DAYS,
} as const;

/**
 * Resolves treasurer-visible body copy for a preflight warning row, with
 * pluralization applied. Returns `null` when no copy entry is defined, in
 * which case the caller falls back to `warning.warningText`.
 */
/**
 * Substitutes the `{N}` count token AND resolves `(s)` pluralization markers so
 * a single-item warning reads "1 expenditure", not "1 expenditure(s)". Every
 * literal `(s)` in the copy templates collapses to "" (count === 1) or "s".
 */
function substituteCount(template: string, count: number): string {
  return template
    .replace('{N}', String(count))
    .replace(/\(s\)/g, count === 1 ? '' : 's');
}

/**
 * Pending-at-filing warning body with correct grammar for the count: the
 * singular variant reads "1 transaction ... isn't ... it's", the plural
 * "{N} transactions ... aren't ... they're". Picks the variant by count,
 * substitutes `{N}` on the plural, and resolves the `{state}` / `{portal}`
 * tokens at the render boundary so the copy composes per jurisdiction with no
 * hard-coded "Alabama" / "FCPA portal" — exactly like
 * {@link pendingAtFilingManualPortalBody}. Same singular/plural shape as
 * {@link resolvePreflightWarningBody}'s PendingInPeriod arm.
 */
export function pendingAtFilingWarningBody(
  count: number,
  stateDisplayName: string,
  portalName: string,
): string {
  const template = count === 1
    ? PreflightCopy.PendingAtFilingWarningBodySingular
    : PreflightCopy.PendingAtFilingWarningBodyPlural.replace('{N}', String(count));
  return template
    .replace('{state}', stateDisplayName)
    .replace('{portal}', portalName);
}

/**
 * §32 — body for the pending rows the electronic export can't carry (AL In-Kind
 * "Other"): Export would say "nothing to export", so the remedy is manual portal
 * entry. Picks the singular/plural variant by count (grammar agrees), substitutes
 * `{N}` on the plural, and resolves the `{state}` / `{portal}` tokens at the
 * render boundary so the copy composes per jurisdiction with no hard-coded
 * "Alabama" / "FCPA portal".
 */
export function pendingAtFilingManualPortalBody(
  count: number,
  stateDisplayName: string,
  portalName: string,
): string {
  const template = count === 1
    ? PreflightCopy.PendingAtFilingManualPortalBodySingular
    : PreflightCopy.PendingAtFilingManualPortalBodyPlural.replace('{N}', String(count));
  return template
    .replace('{state}', stateDisplayName)
    .replace('{portal}', portalName);
}

/**
 * §32 — heading for the "enter these in the portal by hand" panel (CONFIRMED
 * in-kind "Other" rows). Singular/plural so "this"/"these" agrees with the count.
 */
export function manualPortalEntryHeading(
  count: number,
  stateDisplayName: string,
  portalName: string,
): string {
  const template = count === 1
    ? PreflightCopy.ManualPortalEntryHeadingSingular
    : PreflightCopy.ManualPortalEntryHeadingPlural;
  return template
    .replace('{state}', stateDisplayName)
    .replace('{portal}', portalName);
}

/**
 * §32 — body for the "enter these in the portal by hand" panel. Singular/plural so
 * the verb + pronoun agree; the plural also substitutes `{N}`.
 */
export function manualPortalEntryBody(
  count: number,
  stateDisplayName: string,
  portalName: string,
): string {
  const template = count === 1
    ? PreflightCopy.ManualPortalEntryBodySingular
    : PreflightCopy.ManualPortalEntryBodyPlural.replace('{N}', String(count));
  return template
    .replace('{state}', stateDisplayName)
    .replace('{portal}', portalName);
}

/**
 * §32 — the export button's no-op note when the only not-yet-uploaded rows are
 * un-emittable in-kind "Other" (the EDI can't carry them), so "already uploaded"
 * would be a lie. Singular/plural; the plural substitutes `{N}`. `{portal}` at the
 * render boundary.
 */
export function exportDeltaManualPortalNote(count: number, portalName: string): string {
  const template = count === 1
    ? PreflightCopy.ExportDeltaOnlyManualPortalNoteSingular
    : PreflightCopy.ExportDeltaOnlyManualPortalNotePlural.replace('{N}', String(count));
  return template.replace('{portal}', portalName);
}

/**
 * Pending-at-filing confirm-dialog body with correct grammar for the count
 * (singular "1 transaction ... hasn't", plural "{N} transactions ... haven't").
 */
export function pendingAtFilingConfirmBody(count: number): string {
  return count === 1
    ? PreflightCopy.PendingAtFilingConfirmBodySingular
    : PreflightCopy.PendingAtFilingConfirmBodyPlural.replace('{N}', String(count));
}

/**
 * GitHub #81 — the standing AL daily-report window banner body, with `{electionEve}`
 * (the window's end date, the day before the election) substituted so no raw token
 * reaches the UI.
 */
export function alDailyWindowBannerBody(electionEve: string): string {
  return PreflightCopy.AlDailyWindowBannerBody.replace('{electionEve}', electionEve);
}

/**
 * GitHub #81 — the per-day AL daily-report obligation strip body, with the day's
 * combined `{gross}` and the campaign-local `{day}` it reports on substituted so no
 * raw token reaches the UI.
 */
export function alDailyObligationStripBody(gross: string, day: string): string {
  return PreflightCopy.AlDailyObligationStripBody.replace('{gross}', gross).replace('{day}', day);
}

/**
 * ADR-025 — the TERMINAL escape-hatch copy registry keyed by warning code. Under the
 * open string-code contract the generic tile (server-shipped `warning.warningText`) is
 * the DEFAULT rendering; this registry supplies a richer client-side body only for the
 * built-in codes that want one (count substitution, singular/plural). It returns `null`
 * for any code it doesn't recognize — a pack-declared code from a newer backend, or a
 * future state's warning — so the caller falls back to the generic tile. This is the
 * terminal design: a new state's warnings render generically with zero shared-copy edits.
 */
export function resolvePreflightWarningBody(
  warningType: string,
  count: number,
): string | null {
  switch (warningType) {
    case WarningType.PendingInPeriod:
      return count === 1
        ? PreflightCopy.PendingInPeriodSingular
        : PreflightCopy.PendingInPeriodPlural.replace('{N}', String(count));
    case WarningType.AgedOverdueUnsettled:
      return PreflightCopy.AgedOverdueUnsettledBody;
    case WarningType.ConfirmedNotFilableNow:
      return substituteCount(PreflightCopy.ConfirmedNotFilableNowBody, count);
    case WarningType.ZeroRateProcessorHasContributions:
      // The server interpolates {processorName} into the warning's warningText at
      // emission (only it has the processor row). Return null so the caller falls back
      // to that server-rendered text — the FE never has the processor name to substitute,
      // and returning the raw template here would leak a literal {processorName} token.
      return null;
    case WarningType.PendingExpenditureInPeriod:
      return substituteCount(PreflightCopy.PendingExpenditureInPeriodBody, count);
    case WarningType.ExpenditurePurposeOther:
      return substituteCount(PreflightCopy.ExpenditurePurposeOtherBody, count);
    case WarningType.UnresolvedRefundReturn:
      return substituteCount(PreflightCopy.UnresolvedRefundReturnBody, count);
    case WarningType.BeginningCashOnHandDiscontinuity:
      // Whole-period condition (no count substitution).
      return PreflightCopy.BeginningCashOnHandDiscontinuityBody;
    case WarningType.FederalDonorInfoBestEfforts:
      return PreflightCopy.FederalDonorInfoBestEffortsBody;
    case WarningType.FederalDisbursementPurposeMissing:
      // Whole-set condition (no count substitution — the body names "one or more").
      return PreflightCopy.FederalDisbursementPurposeMissingBody;
    case WarningType.FederalLoanBalanceDivergence:
      // GitHub #62 — the server now renders this per-loan and branches on divergence
      // direction, jurisdiction, and lender type, interpolating the forgiven amount +
      // lender name. The FE can't reproduce that, so defer to the server-shipped
      // warningText (like ZeroRateProcessorHasContributions) rather than return a static body.
      return null;
    case WarningType.FederalMissingLoanLenderName:
      // FCG-007 — whole-set condition (the body names "one or more loans"); no count.
      return PreflightCopy.FederalMissingLoanLenderNameBody;
    case WarningType.FederalBankLoanNeedsScheduleC1:
      // Whole-set condition (the body names "one or more loans"); no count.
      return PreflightCopy.FederalBankLoanNeedsScheduleC1Body;
    case WarningType.FederalInKindFromOrganization:
      // Per-contribution condition (the body names "an in-kind"); no count substitution.
      return PreflightCopy.FederalInKindFromOrganizationBody;
    case WarningType.FederalMissingContributorCommitteeId:
      // GitHub #63 / #84 — whole-set condition (the body names "one or more"); no count.
      return PreflightCopy.FederalMissingContributorCommitteeIdBody;
    case WarningType.FederalCommitteeIdRegistrationUnverified:
      // GitHub #84 (Part B) — per-contribution count; the body names "{N} contribution(s)".
      return substituteCount(
        PreflightCopy.FederalCommitteeIdRegistrationUnverifiedBody, count);
    case WarningType.MajorContribution:
      // AFC-005 — per-transaction count (contributions + loans ≥ $20,000).
      return substituteCount(PreflightCopy.MajorContributionBody, count);
    case WarningType.RapidReportGeneratedNotReported:
      // Per-contribution count — rapid file generated but not yet marked reported.
      return substituteCount(PreflightCopy.RapidReportGeneratedNotReportedBody, count);
    case WarningType.AlabamaLoanPortalReview:
      // AFC-008 (Part B) — count of in-scope loans on the filing.
      return substituteCount(PreflightCopy.AlabamaLoanPortalReviewBody, count);
    case WarningType.AlabamaInKindOtherPortalEntry:
      // Portal-forced (2026-06-18) — count of omitted In-Kind "Other" rows.
      return substituteCount(PreflightCopy.AlabamaInKindOtherPortalEntryBody, count);
    case WarningType.NegativeCashOnHand:
      // AFU-010 — whole-period condition (no count substitution).
      return PreflightCopy.NegativeCashOnHandBody;
    case WarningType.AlabamaAmendmentPortalWorkflow:
      // AFU-011 — whole-period condition (no count substitution).
      return PreflightCopy.AlabamaAmendmentPortalWorkflowBody;
    case WarningType.FederalAmendmentMissingReceiptId:
      // FEC-026 (D1) — whole-period condition (no count substitution).
      return PreflightCopy.FederalAmendmentMissingReceiptIdBody;
    case WarningType.FederalReportCodeMissingOrInvalid:
      // FEC-026 (D2) — whole-period condition (no count substitution).
      return PreflightCopy.FederalReportCodeMissingOrInvalidBody;
    case WarningType.FederalRefundPayeeAddressMissing:
      // FEC-026 (D3) — whole-set condition (the body names "one or more refunds";
      // no `{N}` token), like FederalMissingLoanLenderName.
      return PreflightCopy.FederalRefundPayeeAddressMissingBody;
    case WarningType.FederalContributorAddressMissing:
      return substituteCount(PreflightCopy.FederalContributorAddressMissingBody, count);
    // #346 removed WarningType.FederalRefundLineMisroute — refund routing is now explicit per subtype.
    case WarningType.FederalEarmarkedConduitDeferred:
      // ActBlue/WinRed conduit count — the body names "{N} contribution(s)".
      return substituteCount(PreflightCopy.FederalEarmarkedConduitDeferredBody, count);
    case WarningType.FederalAmendmentLoanRemovalDivergence:
      // Whole-set condition (the body names "a loan"); no count substitution.
      return PreflightCopy.FederalAmendmentLoanRemovalDivergenceBody;
    case WarningType.FederalReportDoesNotReconcile:
      // Strategy-3 money-conservation safety net — whole-period condition. The body
      // carries an `{AMOUNT}` token the SERVER fills (only it computed the unaccounted
      // figure), so the rendered warning.warningText is preferred; this returns the
      // template unchanged as the fallback.
      return PreflightCopy.FederalReportDoesNotReconcileBody;
    case WarningType.IndividualContributionOnOrganizationContact:
      // Per-contribution count — the body names "{N} contribution(s)".
      return substituteCount(
        PreflightCopy.IndividualContributionOnOrganizationContactBody, count);
    case WarningType.FederalTransferOutMissingCommitteeId:
      // GitHub #49 — per-expenditure count; the body names "{N} transfer(s)".
      return substituteCount(PreflightCopy.FederalTransferOutMissingCommitteeIdBody, count);
    case WarningType.FederalTransferQualificationUnreviewed:
      // #711 — per-contribution count; the body names "{N} transfer(s)".
      return substituteCount(PreflightCopy.FederalTransferQualificationUnreviewedBody, count);
    case WarningType.FederalReceiptFromVendorContact:
      // F-U3 — per-contribution count; the body names "{N} contribution(s)".
      return substituteCount(PreflightCopy.FederalReceiptFromVendorContactBody, count);
    case WarningType.FederalPartyPairOverCombinedCeiling:
      // #749 — the body names the contributing committees and their figures, which only the
      // SERVER knows (it did the pooling). Return null so the caller falls back to the
      // server-rendered warning.warningText; returning the template here would leak literal
      // {COMMITTEES}… tokens to the treasurer, the FederalContributionOverLimit precedent.
      return null;
    case WarningType.FederalContributionOverLimit:
      // GitHub #61 — the body carries donor/election/figure tokens the SERVER fills (only it
      // knows the per-election aggregate + limit) AND a time-aware 60-day cure countdown the FE
      // can't reproduce. Return null so the caller falls back to the server-rendered
      // warning.warningText — returning the raw template here would DEFEAT that fallback and leak
      // literal {DONOR}… tokens to the treasurer (matching the ZeroRateProcessorHasContributions /
      // FederalLoanBalanceDivergence precedent).
      return null;
    default:
      // ADR-025 — an unknown / pack-declared code carries no bundled body copy; return
      // null so the caller renders the generic tile from the server-shipped warningText.
      return null;
  }
}

// ADR-025 — the per-warning attestation classifier (code + label) is now resolved
// SERVER-side and shipped on `PreflightWarning.classifier` (`{ code, label }`). The FE
// reads it directly, so the former `resolveClassifierLabel` / `resolveClassifierValue`
// warning-identity switches (and their `assertNever` default) are deleted — behavior
// rides the wire, not a hard-coded type-identity table.

/**
 * The persistent "possible-duplicate donor" lead count shown on BOTH the finance
 * dashboard and the close-hub masthead. One source of truth for the copy + its
 * singular/plural so the two surfaces cannot drift. Display-only for the pilot (there
 * is no duplicate-resolution route yet); rendered only when the count is greater than 0.
 */
export function duplicateDonorLeadLabel(count: number): string {
  return `${count} possible duplicate donor${count === 1 ? '' : 's'} to review`;
}

/**
 * #646 — the omission nudge shown on BOTH the "mark filed in portal" confirm dialog and the
 * close-hub Overdue surface: this period holds Confirmed transactions that were never uploaded to
 * the state. One source of truth for the copy + its singular/plural so the two surfaces cannot
 * drift. Rendered only when the count is greater than 0. Soft alert only — it never blocks filing.
 * Frontend-only surface (the server ships the count, not the sentence); asserted verbatim by
 * preflightCopy.test.ts.
 */
export function neverUploadedToStateBody(count: number): string {
  const noun = count === 1 ? 'transaction' : 'transactions';
  const verb = count === 1 ? 'was' : 'were';
  return `${count} confirmed ${noun} in this period ${verb} never uploaded to the state. `
    + 'You can still file — just make sure these are in the portal too, or the state won’t have them.';
}

/**
 * Marking filed records only the rows the generated document carried. A row entered after that
 * generate stays Confirmed, and the confirm dialog names it so the treasurer knows to amend.
 * Rendered only when the count is greater than 0. Soft alert only — it never blocks marking filed.
 */
export function notOnFilingBody(count: number): string {
  return count === 1
    ? '1 transaction entered after you generated isn’t on this filing. Amend to add it.'
    : `${count} transactions entered after you generated aren’t on this filing. Amend to add them.`;
}
